import type { Icons } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import { Icon } from '@ksyos/design-system';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import type { StatusLabelStatus } from '../../../../common/enums/statusLabelStatus';
import type { IReduxState } from '../../../configureStore';
import type { IProcess } from '../../interfaces/process';
import { ServiceType } from '../../../../common/enums/serviceType';
import { Badge } from '../../elements/Badge/Badge';
import { InfoTooltip } from '../../elements/common';
import { getStatusLabel } from '../../elements/getStatusLabel';
import { StatusLabel } from '../../elements/StatusLabel';

interface IProcessItemHeaderProps {
    date?: string | null;
    icon?: Icons;
    isAdministrativeActivity?: boolean;
    isExaminationRequest?: boolean;
    isFirstItem: boolean;
    isFirstProcess?: boolean;
    isItemOpen: boolean;
    onToggle?: (isOpened: boolean) => void;
    organizationName?: string;
    practitionerName?: string;
    process: IProcess | undefined;
    processStatusLabel?: JSX.Element;
    serviceType?: ServiceType;
    showProcessIcon?: boolean;
    showStatusLabel?: boolean;
    status?: string;
    statusLabel?: StatusLabelStatus | null;
    title: string;
    tooltip?: string;
    tooltipHeader?: string;
}

function getIconType(props: IProcessItemHeaderProps) {
    if (props.icon) {
        return props.icon;
    }
    if (props.isExaminationRequest) {
        return ServiceType.inquiry;
    }
    if (props.serviceType === ServiceType.referralSmall) {
        return ServiceType.referral;
    }
    if (props.serviceType === ServiceType.other) {
        return undefined;
    }

    return props.serviceType;
}

export function ProcessItemHeader(props: IProcessItemHeaderProps) {
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);

    let date;
    if (props.date) {
        date = DateTime.from(props.date);
    }

    let statusLabel: StatusLabelStatus | undefined;
    if (props.statusLabel) {
        statusLabel = props.statusLabel;
    } else if (props.process) {
        if (isManager) {
            statusLabel = getStatusLabel(props.process);
        } else {
            statusLabel = props.process.statusLabel;
        }
    }

    const iconColor = props.isAdministrativeActivity || props.isExaminationRequest ? 'primary' : 'secondary';
    const iconType = getIconType(props);

    return (
        <>
            {(props.isFirstItem || props.showProcessIcon) && (
                <span className={`process-icon ${props.isFirstProcess ? 'first' : ''}`}>
                    <Icon
                        color={iconColor}
                        icon={iconType}
                    />
                </span>
            )}
            <div
                className={classnames('form-header level', { open: props.isItemOpen })}
                id={props.process ? props.process?.id : 'newProcess'}
                onClick={async () => (props.onToggle ? props.onToggle(!props.isItemOpen) : null)}
            >
                <div className="form-header__title level-left">
                    <h2>
                        {props.title}
                        {props.tooltip && (
                            <InfoTooltip
                                text={props.tooltip}
                                tooltipHeader={props.tooltipHeader}
                            />
                        )}
                        {props.status && <span className="status">{props.status}</span>}
                    </h2>
                    {(props.isFirstItem || props.showStatusLabel) && (
                        <>
                            {props.processStatusLabel ? (
                                <Badge>{props.processStatusLabel}</Badge>
                            ) : (
                                <StatusLabel
                                    badge
                                    icon={!isManager}
                                    statusLabel={statusLabel}
                                />
                            )}
                            {props.process && props.isFirstItem && (
                                <span className="form-header--pid">PID {props.process.processId}</span>
                            )}
                        </>
                    )}
                </div>
                <div className="form-header__details level-right">
                    <div className="form-header--process-details">
                        <span className="form-header--name">{props.practitionerName}</span>
                        <span className="form-header--name">{props.organizationName}</span>
                    </div>
                    {date && (
                        <time
                            className="date-calendar"
                            dateTime={date.format('YYYY-MM-DD')}
                        >
                            <span>
                                {date.format('D')} {date.format('MMM')}
                            </span>
                            <span>{date.format('YYYY')}</span>
                        </time>
                    )}
                    {!date && <div className="date-calendar" />}
                </div>
            </div>
        </>
    );
}
